'use client'

import { useComponentTranslation } from '@core/i18n'
import {
  Box,
  Divider,
  Flex,
  Icon,
  Progress as ProgressBase,
  Tag,
  Text,
} from '@core/ui-elements'
import { _ICheckingStatus, _ITransferOrderKind } from '@data/types'
import { percentage } from '@utils/calculations'
import {
  CardColors,
  cardColorSchemaList,
  setComponentColor,
} from '@utils/styles'
import {
  Children,
  forwardRef,
  HTMLAttributes,
  PropsWithChildren,
  useMemo,
} from 'react'
import { IoSparklesSharp } from 'react-icons/io5'

export type TransferOrderCardProps = {
  toKind?: _ITransferOrderKind
  startedItems: number
  unavailableItems: number
  totalItems: number
  onCardClick?: () => void
  checkingStatus?: _ICheckingStatus
}

type TransferOrderKindProps = {
  kind: _ITransferOrderKind
}

type TransferOrderProgressProps = {
  isIndeterminate?: boolean
  startedItems: number
  totalItems: number
  unavailableItems: number
}

TransferOrderCard.Header = forwardRef<
  HTMLDivElement,
  PropsWithChildren<HTMLAttributes<HTMLDivElement>>
>(function TransferOrderCardHeader({ children }: PropsWithChildren, ref) {
  return (
    <Flex
      ref={ref}
      alignItems="center"
      borderTopRadius="0.5rem"
      flexWrap="wrap"
      justifyContent={
        Children.count(children) > 1 ? 'space-between' : 'flex-end'
      }
      padding="0.5rem 0.5rem 0.5rem 0.75rem"
      width="100%"
    >
      {children}
    </Flex>
  )
})

TransferOrderCard.Content = forwardRef<
  HTMLDivElement,
  PropsWithChildren<HTMLAttributes<HTMLDivElement>>
>(function TransferOrderCardContent({ children }: PropsWithChildren, ref) {
  return (
    <Flex
      ref={ref}
      backgroundColor="white"
      borderBottomRadius="0.5rem"
      flexDirection="column"
      gap="0.5rem"
      padding="0.5rem 0.5rem 0.5rem 0.75rem"
      width="100%"
    >
      {children}
    </Flex>
  )
})

TransferOrderCard.Kind = forwardRef<
  HTMLDivElement,
  TransferOrderKindProps & HTMLAttributes<HTMLDivElement>
>(function TransferOrderCardKind({ kind }: TransferOrderKindProps, ref) {
  const t = useComponentTranslation('TransferOrderCard')

  const tag = useMemo(
    () => ({
      [_ITransferOrderKind.plus]: (
        <Tag
          alignItems="center"
          backgroundColor="blue.100"
          size="md"
          width="100%"
        >
          <Icon as={IoSparklesSharp} color="blue.500" /> Plus
        </Tag>
      ),
      [_ITransferOrderKind.receive_and_stow]: (
        <Tag size="md" width="100%">
          {t('Receive and Stow')}
        </Tag>
      ),
      [_ITransferOrderKind.cross_docking]: (
        <Tag size="md" width="100%">
          {t('Cross-Docking')}
        </Tag>
      ),
      [_ITransferOrderKind.fresh_cross_docking_flv]: (
        <Tag size="md" width="100%">
          {t('Fresh Cross-Docking FLV')}
        </Tag>
      ),
      [_ITransferOrderKind.fresh_cross_docking_non_flv]: (
        <Tag size="md" width="100%">
          {t('Fresh Cross-Docking Non FLV')}
        </Tag>
      ),
      [_ITransferOrderKind.hub_to_hub]: (
        <Tag size="md" width="100%">
          {t('Hub to Hub')}
        </Tag>
      ),
      [_ITransferOrderKind.reverse_to]: (
        <Tag size="md" width="100%">
          {t('Reverse TO')}
        </Tag>
      ),
      [_ITransferOrderKind.internal_replenishment]: (
        <Tag size="md" width="100%">
          {t('Internal Replenishment')}
        </Tag>
      ),
      [_ITransferOrderKind.unknown]: (
        <Tag size="md" width="100%">
          {t('Undefined')}
        </Tag>
      ),
    }),
    [t]
  )

  return (
    <Flex ref={ref} justifyContent="center" width="100%">
      {tag[kind]}
    </Flex>
  )
})

TransferOrderCard.Progress = forwardRef<
  HTMLDivElement,
  TransferOrderProgressProps & HTMLAttributes<HTMLDivElement>
>(function TransferOrderCardProgress(
  {
    isIndeterminate = false,
    startedItems,
    totalItems,
    unavailableItems,
  }: TransferOrderProgressProps,
  ref
) {
  const t = useComponentTranslation('TransferOrderCard')

  const progressPercentage = useMemo(
    () => percentage(startedItems, totalItems),
    [startedItems, totalItems]
  )

  return (
    <Flex ref={ref} alignItems="center" gap="0.25rem" width="100%">
      <ProgressBase
        borderRadius="31.25rem"
        colorScheme={setComponentColor({
          startedItems,
          totalItems,
          unavailableItems,
        })}
        flex="1"
        height="0.5rem"
        isIndeterminate={isIndeterminate}
        value={progressPercentage}
      />

      <Flex gap="0.25rem">
        <Text fontSize="0.75rem" fontWeight="bold">
          {`${progressPercentage}%`}
        </Text>{' '}
        <Text fontSize="0.75rem">{t('Completed')}</Text>
      </Flex>
    </Flex>
  )
})

TransferOrderCard.Stats = forwardRef<
  HTMLDivElement,
  PropsWithChildren<HTMLAttributes<HTMLDivElement>>
>(function TransferOrderCardStats({ children }: PropsWithChildren, ref) {
  return (
    <Flex ref={ref} alignItems="center" flexDirection="inherit" gap="inherit">
      {children}
    </Flex>
  )
})

TransferOrderCard.Actions = forwardRef<
  HTMLDivElement,
  PropsWithChildren<HTMLAttributes<HTMLDivElement>>
>(function TransferOrderCardActions({ children }: PropsWithChildren, ref) {
  return (
    <Box ref={ref} marginTop="0.5rem">
      {children}
    </Box>
  )
})

TransferOrderCard.Footer = forwardRef<
  HTMLDivElement,
  PropsWithChildren<HTMLAttributes<HTMLDivElement>>
>(function TransferOrderCardFooter({ children }: PropsWithChildren, ref) {
  return (
    <Box ref={ref}>
      <Divider margin="0.75rem 0 1rem" />
      {children}
    </Box>
  )
})

export function TransferOrderCard({
  toKind,
  startedItems,
  unavailableItems,
  totalItems,
  onCardClick,
  children,
  checkingStatus,
}: PropsWithChildren<TransferOrderCardProps>) {
  const getColorByCheckingStatus = (status: _ICheckingStatus): CardColors => {
    const statusColors = {
      [_ICheckingStatus.ready_to_check]: CardColors.gray,
      [_ICheckingStatus.in_progress]: CardColors.yellow,
      [_ICheckingStatus.paused]: CardColors.yellow,
      [_ICheckingStatus.checked]:
        unavailableItems > 0 ? CardColors.yellow : CardColors.green,
      [_ICheckingStatus.canceled]: CardColors.red,
      [_ICheckingStatus.closed]: CardColors.red,
      [_ICheckingStatus.completed]: CardColors.intense_green,
    }

    return statusColors[status] || CardColors.gray
  }

  const color = checkingStatus
    ? getColorByCheckingStatus(checkingStatus)
    : toKind === _ITransferOrderKind.plus
      ? CardColors.blue
      : setComponentColor({ startedItems, totalItems, unavailableItems })

  return (
    <Flex
      backgroundColor={cardColorSchemaList[color].secondary}
      borderLeft="0.25rem solid"
      borderLeftColor={cardColorSchemaList[color].primary}
      borderRadius="0.5rem"
      boxShadow="0 1px 2px 0 rgba(0, 0, 0, 0.06), 0 1px 3px 0 rgba(0, 0, 0, 0.10)"
      cursor="pointer"
      flexDirection="column"
      width="100%"
      onClick={onCardClick}
    >
      {children}
    </Flex>
  )
}
